import { Component, Host, h, Prop, Watch, State, Fragment, Listen, Element } from '@stencil/core';

import { fetchCommentsData } from './fetch-comments';
import store from './store';

export interface UiCmsBlogCommentSectionProps {
  apiKey: string;
  getCommentsApiUrl: string;
  setCommentApiUrl: string;
  deleteCommentApiUrl: string;
  deactivateCommentApiUrl: string;
  emailInfoText: string;
  netiquetteLink: string;
  stayInformedText: string;
  commentsPerPage: number;
  isEditMode: boolean;
}

export interface Comments {
  [key: number]: Comment;
}

export interface Comment {
  commentId: string;
  date: string;
  message: string;
  status: string;
  name: string;
  isManagement: string;
  parentId: string;
  replies: Comment[];
}

@Component({
  tag: 'ui-cms-blog-comment-section',
  styleUrl: 'ui-cms-blog-comment-section.scss',
  shadow: false,
})
export class UiCmsBlogCommentSection {
  @Element() el: HTMLElement;

  @Prop({ mutable: true }) body: UiCmsBlogCommentSectionProps = null;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @Prop() i18n = {
    posts: 'Beiträge',
    reply: 'Antworten',
    close: 'Schließen',
    leaveComment: 'Ihr Kommentar zum Artikel',
    replyToComment: 'Ihre Antwort zu diesem Kommentar',
    name: 'Name',
    email: 'E-Mail',
    message: 'Nachricht',
    sendComment: 'Kommentar senden',
    netiquette: 'Netiquette',
    replyToCommentFrom: 'Antwort zu Kommentar von',
    emptyField: 'Bitte füllen Sie dieses Feld aus',
    invalidEmail: 'Bitte geben Sie eine gültige E-Mail Adresse ein',
    correctFieldsError: 'Bitte korrigieren Sie die als Fehler markierten Felder',
    loadMoreComments: 'Weitere Kommentare Anzeigen',
    hideComments: 'Kommentare verbergen',
    successMessage:
      'Vielen Dank für Ihren Kommentar!<br/><br/>Sie erhalten eine E-Mail mit einem Bestätigungslink. Ihr Kommentar wir erst nach Ihrer Bestätigung veröffentlicht.',
    comment: 'Kommentar:',
    deleteComment: 'Löschen',
    deactivateComment: 'Deaktivieren',
    commentDeleted: 'Kommentar wurde gelöscht',
    commentDeactivated: 'Kommentar wurde deaktiviert',
  };

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  @State() comments: Comments = {};

  @State() shownComments: number;

  @State() loadMoreID = 'mainLoadMore';

  @State() isMainCommentFormOpen = true;

  @Watch('utilityI18n')
  utilityI18nChangeHandler(newValue: string) {
    if (newValue) this.i18n = { ...JSON.parse(newValue) };
  }

  async componentWillLoad() {
    this.utilityI18nChangeHandler(this.utilityI18n);
    this.utilityBodyChangeHandler(this.utilityBody);

    if (this.body?.getCommentsApiUrl && this.body?.apiKey) this.comments = await this.fetchComments();

    this.saveGlobalDataInStore();

    // set initial number of shown comments
    this.shownComments = store.state.commentsPerPage;
  }

  private saveGlobalDataInStore() {
    if (this.body) {
      store.state = {
        ...store.state,
        apiKey: this.body.apiKey,
        getCommentsApiUrl: this.body.getCommentsApiUrl,
        setCommentApiUrl: this.body.setCommentApiUrl,
        deleteCommentApiUrl: this.body.deleteCommentApiUrl,
        deactivateCommentApiUrl: this.body.deactivateCommentApiUrl,
        emailInfoText: this.body.emailInfoText,
        netiquetteLink: this.body.netiquetteLink,
        stayInformedText: this.body.stayInformedText,
        commentsPerPage: this.body.commentsPerPage,
        isEditMode: this.body.isEditMode,
        i18n: this.i18n,
      };
    }
  }

  private async fetchComments(): Promise<Comments> {
    const commentsData = await fetchCommentsData({
      ...(!this.body.isEditMode && { apiKey: this.body.apiKey }),
      apiUrl: this.body.getCommentsApiUrl,
    });

    return commentsData;
  }

  // this handles the offset
  @Listen('loadMoreOffset')
  onLoadMoreChange(ev) {
    // The following checks if event came from inside this component
    if (!this.el.contains(ev.target)) {
      return;
    }
    // update loadmore offset
    this.shownComments = ev.detail.data[this.loadMoreID];
  }

  // this handles the main comment toggle
  @Listen('showMainCommentForm')
  onMainCommentFormChange(ev) {
    // The following checks if event came from inside this component
    if (!this.el.contains(ev.target)) {
      return;
    }
    // update loadmore offset
    this.isMainCommentFormOpen = ev.detail.show;
    store.state.isMainCommentFormOpen = ev.detail.show;
  }

  render() {
    const commentCount = this.comments ? Object.keys(this.comments).length : 0;
    return (
      <Host>
        <span class="comments-count">
          <strong>{`${commentCount} ${store.state.i18n.posts}`}</strong>
        </span>
        {this.comments && Object.keys(this.comments).length > 0 ? (
          <Fragment>
            <div class="comments">
              {Object.keys(this.comments)
                .slice(0, this.shownComments)
                .map((key) => (
                  <ui-cms-blog-comment key={key} commentData={this.comments[key]}></ui-cms-blog-comment>
                ))}
              {store.state.commentsPerPage < Object.keys(this.comments).length && (
                <ui-cms-blog-load-more-comments
                  loadMoreID={this.loadMoreID}
                  totalComments={Object.keys(this.comments).length}
                  initialShownComments={store.state.commentsPerPage}
                ></ui-cms-blog-load-more-comments>
              )}
            </div>
            {this.isMainCommentFormOpen && <ui-cms-add-comment></ui-cms-add-comment>}
          </Fragment>
        ) : (
          this.isMainCommentFormOpen && <ui-cms-add-comment></ui-cms-add-comment>
        )}
      </Host>
    );
  }
}
